import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAssistStream, AppAssistStreamRead, AppAssistStreamRich } from "./AppAssistStream";

// AppAssistStreamPost - ONE streamed `/assist` question over XHR: `OnText` hears the answer so far as the
// response grows (`onprogress`, `AppAssistStream.Read`) - its words and what the stream said of each
// table/chart slot (already cleaned by the service; the panel cleans again) - `Done` the final one-object
// answer exactly once - (0, "") for a lost connection, like `AppAccountPost`. ⛔ Nothing here sends a message.

export type AppAssistStreamText = (Partial: string, Rich: AppAssistStreamRich[]) => void;
export type AppAssistStreamDone = (Status: number, Text: string) => void;

export class AppAssistStreamPost {
    private Url: string;
    private Body: string;
    private OnText: AppAssistStreamText;
    private Done: AppAssistStreamDone;
    private Req: XMLHttpRequest | null = null;
    private Ended: boolean = false;
    private Shown: string = "";

    public constructor(Url: string, Body: string, OnText: AppAssistStreamText, Done: AppAssistStreamDone) {
        this.Url = Url;
        this.Body = Body;
        this.OnText = OnText;
        this.Done = Done;
    }

    public Send = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Req = new XMLHttpRequest();
            (this.Req as XMLHttpRequest).onreadystatechange = this.OnState;
            (this.Req as XMLHttpRequest).onprogress = this.OnProgress;
            (this.Req as XMLHttpRequest).open("POST", this.Url, true);
            (this.Req as XMLHttpRequest).setRequestHeader("Content-Type", "application/json");
            (this.Req as XMLHttpRequest).send(this.Body);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStreamPost.Send");
            this.Finish(0, "");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the page is waiting on these words. */
    private OnProgress = (): void => {
        try {
            if (this.Ended === true || this.Req === null) { return; }
            const Now: AppAssistStreamRead = AppAssistStream.Read((this.Req as XMLHttpRequest).responseText, false);
            // ⚠ A slot's event can come with no new word (a chart's closing line adds no text), so what was
            // shown is the words AND the slots.
            const Seen: string = Now.Partial + "\u0000" + AppAssistStream.RichSig(Now.Rich);
            if (Now.Partial.length === 0 || Seen === this.Shown) { return; }
            this.Shown = Seen;
            this.OnText(Now.Partial, Now.Rich);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStreamPost.OnProgress");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the page is waiting on this answer. */
    private OnState = (): void => {
        try {
            const Req: XMLHttpRequest = this.Req as XMLHttpRequest;
            if (Req.readyState !== 4) { return; }
            const Whole: AppAssistStreamRead = AppAssistStream.Read(Req.responseText, true);
            this.Finish(Req.status, Whole.Final);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStreamPost.OnState");
        }
    };

    /** ⛔ The ONLY place `Done` is called, latched before delivering. */
    private Finish = (Status: number, Text: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the one thing that releases the page.
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            this.Done(Status, Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStreamPost.Finish");
        }
    };
}
